import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { filterToValidAttributes } from '@zocdoc/dom-property';
import { injectIntl } from 'react-intl';

import PaddedContainer from '../../PaddedContainer';
import { breakpoint } from '../../../grid';
import { Link } from '../../../link';
import Zee from '../../../icons/Zee';
import { FontFamily } from '../../../typography';
import getLocalizedContent from '../../../utils/getLocalizedContent';
import { MetricsClickable } from '@zocdoc/frontend-metrics';

const LinkGroup = styled.div`
    > * {
        margin-right: 4px;
    }

    ${breakpoint.small} {
        ${p => p.pushToEnd ? 'margin-left: auto !important' : ''};
    }
`;

const StyledContainer = styled(PaddedContainer)`
    align-items: center;
    background-color: ${p => p.theme.footer.support.bgColor};
    color: ${p => p.theme.footer.support.textColor};
    display: flex;
    flex-wrap: wrap;
    line-height: 2;
    padding-top: 14px;
    padding-bottom: 14px;
`;

const StyledLink = styled(Link)`
    border-bottom-color: ${p => p.theme.footer.support.linkUnderlineColor};
    color: ${p => p.theme.footer.support.linkColor};

    &:focus,
    &:hover {
        border-bottom-color: ${p => p.theme.footer.support.linkUnderlineColorHover};
        color: ${p => p.theme.footer.support.linkColorHover};
    }

    &:visited {
        border-bottom-color: ${p => p.theme.footer.support.linkUnderlineColorVisited};
        color: ${p => p.theme.footer.support.linkColorVisited};
    }
`;

export const BoldStyledLink = styled(StyledLink)`
    font-family: ${FontFamily.SharpSansBold};
`;

const StyledZee = styled(Zee)`
    margin-right: 17px;
    font-size: 17px;

    ${breakpoint.small} {
        font-size: 36px;
    }
`;

export class SupportFooter extends PureComponent {
    static propTypes = {
        theme: PropTypes.shape({
            footer: PropTypes.shape({
                support: PropTypes.shape({
                    leftContent: PropTypes.arrayOf(PropTypes.object),
                    rightContent: PropTypes.arrayOf(PropTypes.object),
                    showZocdocIcon: PropTypes.bool,
                    text: PropTypes.string,
                }),
            }),
        }).isRequired,
        trackLinkClick: PropTypes.func,
    };

    handleBeforeLinkClick = async e => {
        const { trackLinkClick } = this.props;
        if (trackLinkClick) {
            await trackLinkClick(e, e.currentTarget.getAttribute('data-element-name'));
        }
    };

    renderCustomContent = (definition, index) => {
        if (definition.uri) {
            const LinkComponent = (definition.bold) ? BoldStyledLink : StyledLink;

            return (
                <MetricsClickable
                    key={index}
                    action={`Click ${definition.text}`}
                    component={definition.element}
                    element="Link"
                >
                    <LinkComponent
                        beforeClick={this.handleBeforeLinkClick}
                        data-element-name={definition.element}
                        data-test={definition.element ? `support-link-${definition.element}` : undefined}
                        href={definition.uri}
                        target="_blank"
                        title={definition.title}
                    >
                        {definition.text}
                    </LinkComponent>
                </MetricsClickable>
            );
        } else {
            return (<span key={index}>{definition.text}</span>);
        }
    };

    maybeRenderCustomContent(content, pushToEnd = false) {
        if (content) {
            return (
                <LinkGroup pushToEnd={pushToEnd}>
                    {content.map(this.renderCustomContent)}
                </LinkGroup>
            );
        }
    }

    maybeRenderZee(shouldRenderZee) {
        if (shouldRenderZee) {
            return (
                <StyledZee expression="smile" />
            );
        }
    }

    render() {
        const {
            children,
            theme: { footer: { support } },
            theme, intl,
            ...props
        } = this.props;

        const leftContent = getLocalizedContent({
            intl,
            theme,
            path: 'footer.support.leftContent',
        });

        const rightContent = getLocalizedContent({
            intl,
            theme,
            path: 'footer.support.rightContent',
        });

        return (
            <StyledContainer {...filterToValidAttributes(props)}>
                {this.maybeRenderZee(support.showZocdocIcon)}
                {this.maybeRenderCustomContent(leftContent)}
                {this.maybeRenderCustomContent(rightContent, true)}
            </StyledContainer>
        );
    }
}

export default injectIntl(withTheme(SupportFooter));
